import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { App } from "./App.js";
import { OmAuthProvider } from "./components/OmAuthProvider.js";
import { OddsFormatProvider } from "./lib/odds.js";
import { Desk } from "./pages/Desk.js";
import { ContestBrowser } from "./pages/ContestBrowser.js";
import { ContestDetail } from "./pages/ContestDetail.js";
import { Portfolio } from "./pages/Portfolio.js";
import { MarketMaking } from "./pages/MarketMaking.js";
import { Market } from "./pages/Market.js";
import { History } from "./pages/History.js";
import { DepthView } from "./pages/DepthView.js";
import { RequireAccess } from "./components/Gate.js";
import { SCOPES, useIsAuthed } from "./lib/omauth.js";
import { registerServiceWorker } from "./lib/pwa.js";
import { queryClient } from "./lib/queryClient.js";
import { installSessionGuard } from "./lib/session-guard.js";
import "./index.css";

// Register the network-first service worker (production only) so the app is installable.
registerServiceWorker();

// Wipe every scrap of the previous user's state the moment the credential changes. Must be
// installed BEFORE the first render so a sign-in can never paint cached data from whoever
// used this tab last. See lib/session-guard.ts.
installSessionGuard();

/** `/`: the Desk for signed-in users; signed-out visitors land on the public Markets board. */
function Home() {
  const authed = useIsAuthed();
  if (!authed) return <Navigate to="/markets" replace />;
  return (
    <RequireAccess feature="your desk" scope={SCOPES.account}>
      <Desk />
    </RequireAccess>
  );
}

// Dev only: the trade-ticket review page (every state from mock sheet data). `import.meta.env.DEV`
// is a build-time constant, so production builds drop this import — and the page — entirely.
const DevTicket = import.meta.env.DEV ? React.lazy(() => import("./dev/DevTicket.js")) : null;
// Dev only: the Portfolio review page (every state from mock orders/prices; nothing is sent).
const DevPortfolio = import.meta.env.DEV ? React.lazy(() => import("./dev/DevPortfolio.js")) : null;
// Dev only: the Desk review page (mock scenarios; nothing is sent).
const DevDesk = import.meta.env.DEV ? React.lazy(() => import("./dev/DevDesk.js")) : null;
// Dev only: the History review page (mock scenarios; nothing is sent).
const DevHistory = import.meta.env.DEV ? React.lazy(() => import("./dev/DevHistory.js")) : null;
// Dev only: the Market making review page (mock opportunities + a mock quote builder; nothing is sent).
const DevMarketMaking = import.meta.env.DEV ? React.lazy(() => import("./dev/DevMarketMaking.js")) : null;
// Dev only: the Market screen review page (mock books, depth and history; nothing is sent).
const DevMarket = import.meta.env.DEV ? React.lazy(() => import("./dev/DevMarket.js")) : null;
// Dev only: a page that throws on render, to check the app shell's PageErrorBoundary.
function DevCrash(): never {
  throw new Error("Deliberate crash from /dev/crash (checks the page error boundary).");
}

const ACCOUNT_BODY =
  "Your positions, orders, balances and P&L live on your own OpenMarkets account. Sign in to see them — free works.";

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <OddsFormatProvider>
        <OmAuthProvider>
          <BrowserRouter>
            <Routes>
            {DevTicket && (
              <Route
                path="/dev/ticket"
                element={
                  <React.Suspense fallback={null}>
                    <DevTicket />
                  </React.Suspense>
                }
              />
            )}
            {DevPortfolio && (
              <Route
                path="/dev/portfolio"
                element={
                  <React.Suspense fallback={null}>
                    <DevPortfolio />
                  </React.Suspense>
                }
              />
            )}
            {DevDesk && (
              <Route
                path="/dev/desk"
                element={
                  <React.Suspense fallback={null}>
                    <DevDesk />
                  </React.Suspense>
                }
              />
            )}
            {DevHistory && (
              <Route
                path="/dev/history"
                element={
                  <React.Suspense fallback={null}>
                    <DevHistory />
                  </React.Suspense>
                }
              />
            )}
            {DevMarketMaking && (
              <Route
                path="/dev/market-making"
                element={
                  <React.Suspense fallback={null}>
                    <DevMarketMaking />
                  </React.Suspense>
                }
              />
            )}
            {DevMarket && (
              <Route
                path="/dev/market"
                element={
                  <React.Suspense fallback={null}>
                    <DevMarket />
                  </React.Suspense>
                }
              />
            )}
            <Route element={<App />}>
              <Route path="/" element={<Home />} />
              {import.meta.env.DEV && <Route path="/dev/crash" element={<DevCrash />} />}
              {/* Public: signed-out visitors read these from the org-keyed feed. */}
              <Route path="/markets" element={<ContestBrowser />} />
              <Route path="/contests/:id" element={<ContestDetail />} />
              {/* One market: liquidity is public; depth, history and your position each gate on their own. */}
              <Route path="/market" element={<Market />} />
              {/* Account: need a session with account:read. */}
              <Route
                path="/portfolio"
                element={
                  <RequireAccess feature="your account" scope={SCOPES.account} signedOutBody={ACCOUNT_BODY}>
                    <Portfolio />
                  </RequireAccess>
                }
              />
              {/* The old Positions route — kept working for bookmarks and older links. */}
              <Route path="/orders" element={<Navigate to="/portfolio" replace />} />
              <Route
                path="/history"
                element={
                  <RequireAccess feature="your account" scope={SCOPES.account} signedOutBody={ACCOUNT_BODY}>
                    <History />
                  </RequireAccess>
                }
              />
              {/* Market making: reads your opportunities (account:read). Placing needs an order-SDK
                  session, and Real also needs trade:execute — the page checks those like the ticket. */}
              <Route
                path="/market-making"
                element={
                  <RequireAccess feature="market making" scope={SCOPES.account} signedOutBody={ACCOUNT_BODY}>
                    <MarketMaking />
                  </RequireAccess>
                }
              />
              {/* Opportunities folded into Portfolio (plays + the Exposed filter) and History (Bets / Plays). */}
              <Route path="/opportunities" element={<Navigate to="/portfolio" replace />} />
              {/* Signals, Arbitrage and Venue Activity are dropped for now (pages kept in src/pages); old links land on Markets. */}
              <Route path="/signals" element={<Navigate to="/markets" replace />} />
              <Route path="/arbitrage" element={<Navigate to="/markets" replace />} />
              <Route path="/venue-activity" element={<Navigate to="/markets" replace />} />
              {/* Depth ladder moved off /positions/:hash to avoid clashing with the
                  Positions book route. */}
              <Route
                path="/depth/:hash"
                element={
                  <RequireAccess feature="order-book depth" scope={SCOPES.depth}>
                    <DepthView />
                  </RequireAccess>
                }
              />
            </Route>
            </Routes>
          </BrowserRouter>
        </OmAuthProvider>
      </OddsFormatProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
